import {throwError as observableThrowError, Observable, of, switchMap} from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import {HttpClient, HttpResponse, HttpParams, HttpHeaders, HttpErrorResponse} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import { AuthService as AuthServiceSSO } from '@auth0/auth0-angular';

export class NotFoundError extends Error {}
export class ForbiddenError extends Error {}
export class UnauthorizedError extends Error {}

export interface HttpRequestOptions {
    headers?: HttpHeaders;
    reportProgress?: boolean;
    params?: HttpParams;
    withCredentials?: boolean;
    responseType?: 'text' | 'json' | any;
    observe?: 'body' | 'response' | any;
}

export const tryParseJson = ( jsonStr: string | undefined ) => {
    if ( !jsonStr || jsonStr === 'null' ) {
        return null;
    }

    let safeParse : any;

    try {
        safeParse = JSON.parse(jsonStr);
    } catch (err) {
        //error when parsing
        console.log('Error when parsing json', err)
    }

    return safeParse;
};


@Injectable({
    providedIn: 'root',
})
export class HttpService {

    static handleError(error: HttpErrorResponse, nullIf404: boolean = false, authService: AuthService = null): Observable<any> {

        let errorMessage: string;

        if (error.error instanceof ErrorEvent) {
            // Client-side error
            console.error('Client-side HTTP Error: ' + error.error.message);
        } else {
            switch (error.status) {
                case 404:
                    errorMessage = '[ HTTP 404 ] (Not Found) ' + error.url;
                    if (nullIf404) {
                        // console.log('HTTP 404 (expected) - casting to null.');
                        return of(null);
                    }
                    break;
                case 403:
                    errorMessage = '[ HTTP 403 ] (Forbidden) ' + error.url;
                    break;
                case 401:
                    errorMessage = '[ HTTP 401 ] (Not Authorized) ' + error.url;

                    if(authService) {
                        authService.clearToken();
                    }

                    break;
                case 400:
                    errorMessage = '[ HTTP 400 ] (Bad Request) ' + error.url;
                    break;
                default:
                    errorMessage = '[ HTTP ' + error.status + ' ] ' + error.url;
            }

            let userMessage = errorMessage + ' UNKNOWN ERROR';

            if (error.error) {
                if (typeof error.error === 'string') {
                    const json = tryParseJson(error.error);

                    if (json && json.message) {
                        userMessage = json.message;
                    }
                } else {
                    userMessage = error.error.message;
                }
            }

            console.warn(errorMessage, error);
            console.error(userMessage);

            throw error;

        }

        console.error('Unknown error: ', error);
        throw error;
    }

    static extractIdFromLocationHeader(res: HttpResponse<any>, idPattern: RegExp = /([0-9]+)$/): number {

        const locationHeader = res.headers.get('Location') || res.headers.get('location');

        if (!locationHeader) {
            throw new Error('No valid Location header found in response');
        }

        const match = locationHeader.match(idPattern);

        if (!match || match.length < 2) {
            throw new Error('No valid ID found in Location header using pattern ' + idPattern.toString());
        }

        return parseInt(match.pop(), 10);
    }

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private auth0AuthService: AuthServiceSSO,
    ) { }

    private prepareAuthHeaders(headers: HttpHeaders = null): Observable<HttpHeaders> {
        if (localStorage.getItem('isSSOFlow') === 'true') {
            // SSO flow
            return this.auth0AuthService.isAuthenticated$.pipe(
                switchMap(isAuthenticated => {
                    if (isAuthenticated) {
                        return this.auth0AuthService.getAccessTokenSilently().pipe(
                            map(token => {
                                this.authService.handleAuth0Token(token);
                                const authHeader = 'Bearer ' + token;
                                if (headers !== null) {
                                    return headers.set('Authorization', authHeader);
                                } else {
                                    return (new HttpHeaders()).set('Authorization', authHeader);
                                }
                            }),
                            catchError(error => {
                                console.error('Error obtaining token silently', error);
                                return of(headers || new HttpHeaders());
                            })
                        );
                    } else {
                        return of(headers || new HttpHeaders());
                    }
                }),
                catchError(error => {
                    console.error('Error checking isAuthenticated', error);
                    return of(headers || new HttpHeaders());
                })
            );
        } else {
            // old flow
            const token: string = this.authService.getToken();
            const authHeader = 'Bearer ' + token;
            if (headers !== null) {
                return of(headers.set('Authorization', authHeader));
            } else {
                return of((new HttpHeaders()).set('Authorization', authHeader));
            }
        }
    }

    get<T>(url: string, options: HttpRequestOptions = {}, nullIf404: boolean = false): Observable<T> {
        return this.prepareAuthHeaders(options.headers).pipe(
            switchMap(headers => {
                headers = headers.append('Pragma', 'no-cache');
                headers = headers.append('Cache-Control', 'private, max-age=0');
                return this.httpClient.get<T>(
                    url,
                    Object.assign({}, options, {
                        headers: headers,
                    })
                ).pipe(
                    catchError((error) => HttpService.handleError(error, nullIf404, this.authService))
                );
            })
        );
    }

    // post(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
    //
    //     console.log("POST", url, body, options);
    //     return this.httpClient.post(
    //         url,
    //         body,
    //         Object.assign({}, options, {
    //             headers: this.prepareAuthHeaders(options.headers),
    //             observe: 'response',
    //             responseType: 'text'
    //         })
    //     )
    //         .pipe(
    //             catchError((error) => {
    //                 console.log("POST", url, body, options);
    //                 console.warn("HTTP Error", error);
    //                 return HttpService.handleError( error, false, this.authService)
    //             })
    //         );
    //
    // }

    post(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
        return this.prepareAuthHeaders(options.headers).pipe(
            switchMap(headers => {
                return this.httpClient.post(
                    url,
                    body,
                    Object.assign({}, options, {
                        headers: headers,
                        observe: 'response',
                        responseType: 'text'
                    })
                ).pipe(
                    catchError((error) => {
                        console.warn("HTTP Error", error);
                        return HttpService.handleError(error, false, this.authService);
                    })
                );
            })
        );
    }


    // put(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
    //     console.log("PUT", url, body, options);
    //
    //     return this.httpClient.put(
    //         url,
    //         body,
    //         Object.assign({}, options, {
    //             headers: this.prepareAuthHeaders(options.headers),
    //             observe: 'response',
    //             responseType: 'text'
    //         })
    //     )
    //         .pipe(
    //             catchError( ( error ) => HttpService.handleError( error, false, this.authService ))
    //         );
    //
    // }


    put(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
        return this.prepareAuthHeaders(options.headers).pipe(
            switchMap(headers => {
                return this.httpClient.put(
                    url,
                    body,
                    Object.assign({}, options, {
                        headers: headers,
                        observe: 'response',
                        responseType: 'text'
                    })
                ).pipe(
                    catchError((error) => HttpService.handleError(error, false, this.authService))
                );
            })
        );
    }

    // patch(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
    //     console.log("PATCH", url, body, options);
    //
    //     return this.httpClient.patch(
    //         url,
    //         body,
    //         Object.assign({}, options, {
    //             headers: this.prepareAuthHeaders(options.headers),
    //             observe: 'response',
    //             responseType: 'text'
    //         })
    //     )
    //         .pipe(
    //             catchError( ( error ) => HttpService.handleError( error, false, this.authService ))
    //         );
    //
    // }

    patch(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
        return this.prepareAuthHeaders(options.headers).pipe(
            switchMap(headers => {
                return this.httpClient.patch(
                    url,
                    body,
                    Object.assign({}, options, {
                        headers: headers,
                        observe: 'response',
                        responseType: 'text'
                    })
                ).pipe(
                    catchError((error) => HttpService.handleError(error, false, this.authService))
                );
            })
        );
    }

    // delete(url: string, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
    //
    //     return this.httpClient.delete(
    //         url,
    //         Object.assign({}, options, {
    //             headers: this.prepareAuthHeaders(options.headers),
    //             observe: 'response',
    //             responseType: 'text'
    //         })
    //     )
    //         .pipe(
    //             catchError( ( error ) => HttpService.handleError( error, false, this.authService ))
    //         );
    // }

    delete(url: string, options: HttpRequestOptions = {}): Observable<HttpResponse<string>> {
        return this.prepareAuthHeaders(options.headers).pipe(
            switchMap(headers => {
                return this.httpClient.delete(
                    url,
                    Object.assign({}, options, {
                        headers: headers,
                        observe: 'response',
                        responseType: 'text'
                    })
                ).pipe(
                    catchError((error) => HttpService.handleError(error, false, this.authService))
                );
            })
        );
    }
}
